import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Moment from "react-moment";
import authService from "../../services/auth.service";
import courses from "../AdmissionFom/courses";

/* ---------- Print CSS (unchanged from your original, kept as a constant) ---------- */
const PRINT_STYLES = `
  li { margin: 0; padding: 0; }
  p { margin: 0; padding: 0; }
  h1,h2,h3,h4,h5,h6 { margin: 0; padding: 0; }

  .Print-chalan-page-container-modal { border: 1px solid lightgray; height: 900px; }
  .Print-chalan-page-header {
    display: flex; flex-direction: row; justify-content: space-between;
    align-items: center; text-align: center; border-bottom: 1px solid lightgray;
    margin-bottom: 10px; padding: 10px;
  }
  .Print-chalan-page-header > img { width: 16%; }
  .Print-chalan-page-title { margin-bottom: 8px; color: #2c3e50; font-size: 23px; font-weight: 600; }
  .Print-chalan-page-content { padding: 10px 10px; }
  .print-chalan-page-box { display: grid; grid-template-columns: 1fr 150px; }
  .print-chalan-page-student-details > ul { display: flex; gap: 10px; flex-direction: column; }
  .print-chalan-page-student-details > ul > li > div { display: flex; flex-direction: row; gap: 30px; }
  .print-chalan-page-student-details > ul > li > div > p:first-child { font-weight: 600; }
  .Print-chalan-page-section-title {
    margin-top: 0; color: #2c3e50; border-bottom: 1px solid lightgray;
    padding: 20px 0; text-align: center; margin-bottom: 0;
  }
  .Print-chalan-page-details {
    display: flex; flex-direction: row; justify-content: space-between;
    flex-wrap: wrap; gap: 10px; padding: 20px;
  }
  .Print-chalan-page-detail-item { width: calc(90%/2); display: flex; flex-direction: row; gap: 10px; }
  .Print-chalan-page-detail-value { margin-bottom: 0; text-align: end; }
  .Print-chalan-detail-label { font-weight: 600; }
  .Print-chalan-printable-content { display: block; padding: 20px; max-width: 900px; margin: auto; }
  .print-chalan-student-profile-image { width: 60%; }
  .print-chalan-student-signature-image { width: 60%; }
  .Signature-box { border: 1px solid black; padding: 30px 100px; }
  .Print-chalan-subtitle { margin-bottom: 10px; }

  @media print {
    body { padding: 15px; }
    .Print-chalan-section, .Print-chalan-image-container { page-break-inside: avoid; }
    li { list-style-type: none; }
    .Chalan-page-auth {
      display: flex; justify-content: end; position: relative; bottom: 0;
    }
    .Chalan-page-auth > ul { display: flex; flex-direction: column; gap: 10px; }
    .Chalan-page-auth > ul > li { display: flex; flex-direction: row; gap: 10px; }
    .Chalan-page-auth > ul > li > input { margin: 0; }
    .Chalan-page-auth > ul > span { border: 1px solid black; padding: 10px 40px; }
  }
`;

const PrintComponent = () => {
  const { id } = useParams();
  const contentRef = useRef(null);
  const dispatch = useDispatch();

  /* ---------- State ---------- */
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ---------- Redux (used only for fallback/aux data like course_name) ---------- */
  const studentInformation = useSelector((state) => state.auth.studentData);

  useEffect(() => {
    authService.getUser(dispatch);
  }, [dispatch]);

  /* ---------- Fetch transaction from API ---------- */
  useEffect(() => {
    if (!id) return;

    let isMounted = true;
    const controller = new AbortController();

    const fetchTransaction = async () => {
      setLoading(true);
      setError(null);
      try {
        // 🔁 Adjust this URL to match your actual backend route
        const res = await fetch(
          `https://campus.acetians.in/api/v2/transaction/${id}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const json = await res.json();
        if (isMounted) setApiData(json);
      } catch (err) {
        if (err.name !== "AbortError" && isMounted) setError(err.message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchTransaction();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [id]);

  /* ---------- Derived data (safe, memoized) ---------- */
  const transaction = apiData?.transaction;
  const student = apiData?.student_details;
  const campus = apiData?.campus_details?.[0];
  const challan = apiData?.challan_details;

  const productParts = useMemo(
    () => (transaction?.productinfo || "").split("-"),
    [transaction?.productinfo]
  );
  const courseCode = productParts[0] || studentInformation?.course_name || "";
  const yearCode = productParts[1] || "";

  const courseType =
    yearCode === "1" ? "New" : yearCode === "2" ? "Renewal" : "Renewal";
  const courseYear =
    yearCode === "1" ? "First Year" : yearCode === "2" ? "Second Year" : "Third Year";

  const academicYear = useMemo(() => {
    const dateStr = transaction?.addedon;
    if (!dateStr) return `${challan?.session || ""}`;
    const year = Number(dateStr.split(" ")[0]?.split("-")[0]);
    return year ? `${year}-${String(year + 1).slice(-2)}` : "";
  }, [transaction?.addedon, challan?.session]);

  /* ---------- Print handler ---------- */
  const handlePrint = useCallback(() => {
    setTimeout(() => {
      const printContent = contentRef.current;
      if (!printContent) return;

      const printWindow = window.open("", "_blank", "width=1000,height=800");
      if (!printWindow) return;

      printWindow.document.write(`
        <html>
          <head>
            <title>Student Fee Chalan</title>
            <style>${PRINT_STYLES}</style>
          </head>
          <body>${printContent.innerHTML}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
      };
    }, 100);
  }, []);

  /* ---------- Loading / error states ---------- */
  if (loading) return <div style={{ padding: 20 }}>Loading transaction…</div>;

  if (error) {
    return (
      <div style={{ padding: 20, color: "red" }}>
        Failed to load transaction: {error}
      </div>
    );
  }

  if (!apiData || !apiData.has_transaction) {
    return <div style={{ padding: 20 }}>Transaction not found.</div>;
  }

  /* ---------- Fallback images (from campus_details) ---------- */
  const collegeLogo =
    campus?.college_logo ||
    "https://campus.acetians.in/custom/images/1722342842lalitpurlogo.png";
  const affiliationLogo =
    campus?.affiliation_logo ||
    "https://images.seeklogo.com/logo-png/21/1/uttar-pradesh-government-logo-png_seeklogo-217343.png";

  return (
    <div
      style={{ padding: "20px", maxWidth: "900px", margin: "auto" }}
      className="Print-chalan-main-container no-print"
    >
      <button className="print-button-chalan-page" onClick={handlePrint}>
        Print {id}
      </button>

      <div ref={contentRef} className="Print-chalan-printable-content">
        <div className="Print-chalan-page-container-modal">
          {/* ---------- Header ---------- */}
          <div className="Print-chalan-page-header">
            <img src={collegeLogo} alt="College Logo" />
            <div>
              <h2 className="Print-chalan-page-title">
                {campus?.college_name ||
                  "रघुवीर सिंह राजकीय महाविद्यालय, ललितपुर,(उo.प्रo)"}
              </h2>
              <p className="Print-chalan-subtitle">
                {campus?.college_description || "नैक ग्रेड – बी"}
              </p>
              <h2 className="Print-chalan-page-title">पंजीकरण फॉर्म</h2>
              <p className="Print-chalan-subtitle">Academic Year {academicYear}</p>
            </div>
            <img src={affiliationLogo} alt="Affiliation Logo" />
          </div>

          {/* ---------- Student details ---------- */}
          <div className="Print-chalan-page-content">
            <div className="print-chalan-page-box">
              <div className="print-chalan-page-student-details">
                <ul>
                  <li>
                    <div>
                      <p>नाम:</p>
                      <p>{student?.name || ""}</p>
                    </div>
                  </li>
                  <li>
                    <div>
                      <p>श्रेणी:</p>
                      <p>{student?.category || ""}</p>
                    </div>
                  </li>
                  <li>
                    <div>
                      <p>लिंग:</p>
                      <p>{student?.gender || ""}</p>
                    </div>
                  </li>
                  <li>
                    <div>
                      <p>प्रवेश का प्रकार:</p>
                      <p>{courseType}</p>
                    </div>
                  </li>
                  <li>
                    <div>
                      <p>पाठ्यक्रम का नाम:</p>
                      <p>{courseCode}</p>
                    </div>
                  </li>
                  <li>
                    <div>
                      <p>पाठ्यक्रम का वर्ष:</p>
                      <p>{courseYear}</p>
                    </div>
                  </li>
                  <li>
                    <div>
                      <p>छात्र हस्ताक्षर:</p>
                      <span className="Signature-box"></span>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="print-chalan-image-box">
                <img
                  className="print-chalan-student-profile-image"
                  src={
                    student?.profile_photo ||
                    "https://i-be-c.org/wp-content/uploads/2016/03/silhouette_speaker.jpg"
                  }
                  alt="Student Profile"
                />
              </div>
            </div>

            {/* ---------- Fee details ---------- */}
            <div className="Print-chalan-page-section">
              <h3 className="Print-chalan-page-section-title">
                {transaction.title} Details
              </h3>

              <div className="Print-chalan-page-details">
                <div className="Print-chalan-page-detail-item">
                  <p className="Print-chalan-detail-label">दिनांक:</p>
                  <p className="Print-chalan-detail-value">
                    {transaction?.addedon ? (
                      <Moment format="DD/MM/YYYY">{transaction.addedon}</Moment>
                    ) : (
                      "-"
                    )}
                  </p>
                </div>

                <div className="Print-chalan-page-detail-item">
                  <p className="Print-chalan-detail-label">
                    ऑनलाइन भुगतान संख्या:
                  </p>
                  <p className="Print-chalan-detail-value">
                    {transaction?.txnid || "-"}
                  </p>
                </div>

                <div className="Print-chalan-page-detail-item">
                  <p className="Print-chalan-detail-label">समय:</p>
                  <p className="Print-chalan-detail-value">
                    {transaction?.addedon?.split(" ")[1] || "-"}
                  </p>
                </div>

                <div className="Print-chalan-page-detail-item">
                  <p className="Print-chalan-detail-label">भुगतान माध्यम:</p>
                  <p className="Print-chalan-detail-value">
                    {transaction?.mode || "-"}
                  </p>
                </div>

                <div className="Print-chalan-page-detail-item">
                  <p className="Print-chalan-detail-label">
                    जमा किया गया शुल्क:
                  </p>
                  <p className="Print-chalan-detail-value">
                    {`₹${transaction?.amount || "0"}`}
                  </p>
                </div>

                <div className="Print-chalan-page-detail-item">
                  <p className="Print-chalan-detail-label">स्थिति:</p>
                  <p className="Print-chalan-detail-value">
                    {transaction?.status || "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* ---------- Auth ---------- */}
            <div className="Chalan-page-auth">
              <ul>
                <li>
                  <input type="checkbox" />
                  <label>Approved</label>
                </li>
                <li>
                  <input type="checkbox" />
                  <label>Declined</label>
                </li>
                <span>Signature of Admission In-Charge</span>
              </ul>
            </div>

            {/* ---------- Paid / Unpaid stamp ---------- */}
            <div>
              {transaction?.status === "success" ? (
                <img
                  style={{ width: "80px" }}
                  src="https://img.magnific.com/free-vector/paid-stamp_1017-8234.jpg?semt=ais_hybrid&w=740&q=80"
                  alt="Paid"
                />
              ) : (
                <img
                  style={{ width: "80px" }}
                  src="https://cdn.creativefabrica.com/2021/03/20/Unpaid-Rubber-Stamp-Graphics-9790875-1.jpg"
                  alt="Unpaid"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintComponent;